import { ReactElement, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { useHotkeys } from 'react-hotkeys-hook'
import { v4 } from 'uuid'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import { IconButton, Tooltip } from '@mui/material'
import { UseAgentUpdate, useAgent, useCopilotKit } from '@copilotkit/react-core/v2/headless'
import { useIsMutating } from '@tanstack/react-query'
import { AI_CHAT_AGENT_ID } from 'src/constants/aiChat'
import useUIStore from 'src/state/stores/ui'
import useRefreshOnProjectChange from '../hooks/useRefreshOnProjectChange'
import useRememberThread from '../hooks/useRememberThread'
import useResponseNotifications from '../hooks/useResponseNotifications'
import useRunErrorNotifications from '../hooks/useRunErrorNotifications'
import useSceneFocusOnAgentEvents from '../hooks/useSceneFocusOnAgentEvents'
import { ChatModelOption } from '../modelAnimals'
import {
  AIChatRunProperties,
  approvalModeFor,
  createChatHost,
  isAiChatUpload,
  localAgentId,
  resolveSelectedModel,
} from '../utils'
import ChatBody from './ChatBody'
import ChatDrawer from './ChatDrawer'
import ChatHeaderActions from './ChatHeaderActions'
import ChatHostOutlet from './ChatHostOutlet'
import RunStatusBadge from './RunStatusBadge'

interface Props {
  projectId: string
  threadId: string
  models: ChatModelOption[]
  defaultModel: string | undefined
  selectedModel: string | undefined
  autoApprove: boolean
  runProperties: AIChatRunProperties
}

/**
 * Button, running badge and chat for one project. This is the single place
 * that registers the thread-scoped CopilotKit agent: a second `useAgent`
 * with the same id would throw, so the panel receives the agent as a prop.
 * Mounted per project inside its CopilotKit provider (`AIChatSession`), in
 * the navbar, so a running turn goes on across pages.
 *
 * Open, the chat replaces the content of the page's right drawer (portalled
 * into its `AIChatDrawerSlot`). Pages without a right drawer (Pläne, PDF)
 * offer no slot, so the button is not shown there; the session stays mounted
 * all the same. Once opened, the chat stays mounted in its own DOM node
 * (`createChatHost`), which is moved into the drawer while shown: closing the
 * chat or changing the page neither detaches it from a running turn nor drops
 * its approval cards and their uploads.
 */
const AIChatWidgetContent = ({
  projectId,
  threadId,
  models,
  defaultModel,
  selectedModel,
  autoApprove,
  runProperties,
}: Props): ReactElement => {
  const setAiChatThreadId = useUIStore(state => state.setAiChatThreadId)
  const aiChatOpen = useUIStore(state => state.aiChatOpen)
  const setAiChatOpen = useUIStore(state => state.setAiChatOpen)
  const portalRootAiChat = useUIStore(state => state.portalRootAiChat)
  const setAiChatModel = useUIStore(state => state.setAiChatModel)
  const setAiChatAutoApprove = useUIStore(state => state.setAiChatAutoApprove)
  const [chatHost] = useState(createChatHost)
  // The chat stays mounted once the project has a remembered thread (from its
  // first turn on, see useRememberThread), so closing it keeps a running turn
  // and pending cards; until then it mounts only while open.
  const threadStored = useUIStore(state => state.aiChatThreadIdByProject[projectId] !== undefined)
  const chatMounted = aiChatOpen || threadStored
  const isUploading =
    useIsMutating({ predicate: mutation => isAiChatUpload(mutation.options.meta) }) > 0

  const { copilotkit } = useCopilotKit()
  const { agent, isReady } = useAgent({
    agentId: localAgentId(threadId),
    threadId,
    runtimeAgentId: AI_CHAT_AGENT_ID,
    updates: [UseAgentUpdate.OnMessagesChanged, UseAgentUpdate.OnRunStatusChanged],
  })

  // The thread-scoped registration happens in an effect after render, so on a
  // thread switch one render still carries the previous thread's agent. The
  // prebuilt chat looks its agent up by id synchronously and errors if it is
  // missing, so the panel waits until the agent for *this* thread is there.
  const panelReady = isReady && agent.agentId === localAgentId(threadId)

  useRefreshOnProjectChange(projectId, agent)
  useSceneFocusOnAgentEvents(projectId, agent)
  useRunErrorNotifications(agent)
  useRememberThread(projectId, threadId, agent)
  useResponseNotifications(agent)

  const handleToggle = useCallback(() => {
    setAiChatOpen(!aiChatOpen)
  }, [aiChatOpen, setAiChatOpen])

  const handleClose = () => setAiChatOpen(false)

  // Like the scene's Shift shortcuts; react-hotkeys-hook ignores it while
  // typing in a field (the chat's own input included).
  useHotkeys('shift+c', handleToggle, { enabled: portalRootAiChat !== null }, [handleToggle])

  const handleModelChange = (model: string) => setAiChatModel(threadId, model)

  const handleAutoApproveChange = (checked: boolean) => {
    setAiChatAutoApprove(threadId, checked)
    // The provider applies its new `properties` only in an effect after the
    // next render: set the core's properties now too, so a turn resumed right
    // after the switch already runs with the new approval mode.
    copilotkit.setProperties({ ...runProperties, approvalMode: approvalModeFor(checked) })
  }

  const handleNewThread = () => {
    const nextThreadId = v4()
    // A new chat keeps the model currently selected and asks before changes.
    if (selectedModel) setAiChatModel(nextThreadId, selectedModel)
    setAiChatThreadId(projectId, nextThreadId)
  }

  // Switching re-registers the thread-scoped agent and remounts the panel,
  // which restores that thread's persisted messages.
  const handleSelectThread = (selected: string, threadModel: string | null | undefined) => {
    const remembered = useUIStore.getState().aiChatModelByThread[selected]
    const model = resolveSelectedModel(models, remembered ?? threadModel, defaultModel)
    if (model) setAiChatModel(selected, model)
    setAiChatThreadId(projectId, selected)
  }

  const actions = (
    <ChatHeaderActions
      projectId={projectId}
      threadId={threadId}
      isRunning={agent.isRunning}
      isUploading={isUploading}
      models={models}
      selectedModel={selectedModel}
      onModelChange={handleModelChange}
      onSelectThread={handleSelectThread}
      onNewThread={handleNewThread}
    />
  )
  const body = (
    <ChatBody
      projectId={projectId}
      threadId={threadId}
      agent={agent}
      panelReady={panelReady}
      autoApprove={autoApprove}
      onAutoApproveChange={handleAutoApproveChange}
      onSelectThread={handleSelectThread}
    />
  )

  return (
    <>
      {portalRootAiChat && (
        <Tooltip title={aiChatOpen ? 'KI-Chat schließen [Shift+C]' : 'KI-Chat [Shift+C]'}>
          <IconButton
            size="small"
            aria-label="KI-Chat"
            aria-pressed={aiChatOpen}
            sx={{ color: 'rgba(0, 0, 0, 0.54)', width: 32, height: 32 }}
            onClick={handleToggle}
            data-cy="ai-chat-button"
          >
            <RunStatusBadge isRunning={agent.isRunning}>
              <ForumOutlinedIcon fontSize="small" />
            </RunStatusBadge>
          </IconButton>
        </Tooltip>
      )}

      {chatMounted && createPortal(body, chatHost)}

      {aiChatOpen &&
        portalRootAiChat &&
        createPortal(
          <ChatDrawer actions={actions} onClose={handleClose}>
            <ChatHostOutlet host={chatHost} />
          </ChatDrawer>,
          portalRootAiChat,
        )}
    </>
  )
}

export default AIChatWidgetContent
